<template>
  <Dialog
    v-model="dialogVisible"
    :title="isEditMode ? 'Edit Medication' : 'Add Medication'"
    dialog-type="primary"
    dialog-size="small"
    :close-on-overlay="false"
    :z-index="3001"
    @close="closeDialog"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 18 18" fill="none">
        <path d="M5.625 18C4.05833 18 2.72917 17.4542 1.6375 16.3625C0.545833 15.2708 0 13.9417 0 12.375C0 11.625 0.141667 10.9083 0.425 10.225C0.708333 9.54167 1.11667 8.93333 1.65 8.4L8.4 1.65C8.93333 1.11667 9.54167 0.708333 10.225 0.425C10.9083 0.141667 11.625 0 12.375 0C13.9417 0 15.2708 0.545833 16.3625 1.6375C17.4542 2.72917 18 4.05833 18 5.625C18 6.375 17.8583 7.09167 17.575 7.775C17.2917 8.45833 16.8833 9.06667 16.35 9.6L9.6 16.35C9.06667 16.8833 8.45833 17.2917 7.775 17.575C7.09167 17.8583 6.375 18 5.625 18ZM12.275 10.85L14.95 8.2C15.2833 7.86667 15.5417 7.475 15.725 7.025C15.9083 6.575 16 6.10833 16 5.625C16 4.625 15.6458 3.77083 14.9375 3.0625C14.2292 2.35417 13.375 2 12.375 2C11.8917 2 11.425 2.09167 10.975 2.275C10.525 2.45833 10.1333 2.71667 9.8 3.05L7.15 5.725L12.275 10.85ZM5.625 16C6.10833 16 6.575 15.9083 7.025 15.725C7.475 15.5417 7.86667 15.2833 8.2 14.95L10.85 12.275L5.725 7.15L3.05 9.8C2.71667 10.1333 2.45833 10.525 2.275 10.975C2.09167 11.425 2 11.8917 2 12.375C2 13.375 2.35417 14.2292 3.0625 14.9375C3.77083 15.6458 4.625 16 5.625 16Z" fill="white"/>
      </svg>
    </template>

    <div class="medication-form-body">
      <!-- 2x3 Grid Layout -->
      <div class="form-grid">
        <!-- Row 1, Col 1: Medication Name -->
        <div class="form-field">
          <label class="field-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="18" viewBox="0 0 14 18" fill="none" class="label-icon">
              <path d="M5.5 12H3V9H5.5V6.5H8.5V9H11V12H8.5V14.5H5.5V12ZM14 5V16C14 17.1 13.1 18 12 18H2C0.9 18 0 17.1 0 16V5C0 3.9 0.9 3 2 3H12C13.1 3 14 3.9 14 5ZM12 5H2V16H12V5ZM13 0H1V2H13V0Z" fill="#374957"/>
            </svg>
            Medication Name <span class="required-text">(Required)</span>
          </label>
          <el-autocomplete
            v-model="formData.medicationName"
            :fetch-suggestions="queryMedications"
            placeholder="Search medication"
            class="medication-form-select w-full"
            popper-class="medication-autocomplete-popper"
            :trigger-on-focus="true"
            :highlight-first-item="true"
            :clearable="true"
            @select="handleMedicationSelect"
            @clear="handleMedicationClear"
          >
            <template #suffix>
              <svg v-if="!formData.medicationName" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="search-icon">
                <path d="M14.2939 12.5786H13.3905L13.0703 12.2699C14.191 10.9663 14.8656 9.27387 14.8656 7.43282C14.8656 3.32762 11.538 0 7.43282 0C3.32762 0 0 3.32762 0 7.43282C0 11.538 3.32762 14.8656 7.43282 14.8656C9.27387 14.8656 10.9663 14.191 12.2699 13.0703L12.5786 13.3905V14.2939L18.2962 20L20 18.2962L14.2939 12.5786ZM7.43282 12.5786C4.58548 12.5786 2.28702 10.2802 2.28702 7.43282C2.28702 4.58548 4.58548 2.28702 7.43282 2.28702C10.2802 2.28702 12.5786 4.58548 12.5786 7.43282C12.5786 10.2802 10.2802 12.5786 7.43282 12.5786Z" fill="#374957"/>
              </svg>
            </template>
          </el-autocomplete>
        </div>

        <!-- Row 1, Col 2: Dosage -->
        <div class="form-field">
          <label class="field-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="label-icon">
              <path d="M11.5 5.25H8.5V3.75H9C9.55 3.75 10 3.3 10 2.75C10 2.2 9.55 1.75 9 1.75H6C5.45 1.75 5 2.2 5 2.75C5 3.3 5.45 3.75 6 3.75H6.5V5.25H3.5C2.95 5.25 2.5 5.7 2.5 6.25C2.5 6.8 2.95 7.25 3.5 7.25V14.75C3.5 15.85 4.4 16.75 5.5 16.75H6.5V20.75L8.5 22.25V16.75H9.5C10.6 16.75 11.5 15.85 11.5 14.75V7.25C12.05 7.25 12.5 6.8 12.5 6.25C12.5 5.7 12.05 5.25 11.5 5.25ZM9.5 8.75H7.75C7.34 8.75 7 9.09 7 9.5C7 9.91 7.34 10.25 7.75 10.25H9.5V11.75H7.75C7.34 11.75 7 12.09 7 12.5C7 12.91 7.34 13.25 7.75 13.25H9.5V14.75H5.5V7.25H9.5V8.75ZM20 10.25V9.75C20.55 9.75 21 9.3 21 8.75C21 8.2 20.55 7.75 20 7.75H15C14.45 7.75 14 8.2 14 8.75C14 9.3 14.45 9.75 15 9.75V10.25C15 10.75 13.5 11.41 13.5 13.25V19.75C13.5 20.85 14.4 21.75 15.5 21.75H19.5C20.6 21.75 21.5 20.85 21.5 19.75V13.25C21.5 11.41 20 10.75 20 10.25ZM17 10.25V9.75H18V10.25C18 11.85 19.5 12.25 19.5 13.25V13.75H15.5C15.5 13.54 15.5 13.36 15.5 13.25C15.5 12.25 17 11.85 17 10.25ZM19.5 15.25V16.75H15.5C15.5 16.24 15.5 15.73 15.5 15.25H19.5ZM15.5 19.75C15.5 19.75 15.5 19.12 15.5 18.25H19.5V19.75H15.5Z" fill="#374957"/>
            </svg>
            Dosage
          </label>
          <TextInput
            v-model="formData.dose"
            size="medium"
            :clearable="false"
            placeholder="e.g. 10 mg"
          />
        </div>

        <!-- Row 2, Col 1: Frequency Number -->
        <div class="form-field">
          <label class="field-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="label-icon">
              <path d="M19.5 11V5C19.5 3.9 18.6 3 17.5 3H16.5V1H14.5V3H6.5V1H4.5V3H3.5C2.4 3 1.5 3.9 1.5 5V19C1.5 20.1 2.4 21 3.5 21H10.5V19H3.5V9H17.5V11H19.5ZM17.5 7H3.5V5H17.5V7ZM14.14 19C14.57 20.45 15.91 21.5 17.5 21.5C19.43 21.5 21 19.93 21 18C21 16.07 19.43 14.5 17.5 14.5C16.55 14.5 15.68 14.88 15.05 15.5H16.5V17H12.5V13H14V14.43C14.9 13.55 16.14 13 17.5 13C20.26 13 22.5 15.24 22.5 18C22.5 20.76 20.26 23 17.5 23C15.08 23 13.06 21.28 12.6 19H14.14Z" fill="#374957"/>
            </svg>
            Frequency Number
          </label>
          <TextInput
            v-model="formData.frequencyNumber"
            size="medium"
            :clearable="false"
            placeholder="Number"
          />
        </div>

        <!-- Row 2, Col 2: per dropdown -->
        <div class="form-field frequency-selector">
          <SingleSelector
            v-model="formData.frequencyPeriod"
            placeholder="Select Per"
            :options="frequencyPeriodOptions"
            size="medium"
          />
        </div>

        <!-- Row 3: Duration -->
        <div class="form-field-row">
          <label class="field-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="label-icon">
              <path d="M11 8V13L15.25 15.52L16.02 14.24L12.5 12.15V8H11ZM21 10V3L18.36 5.64C16.74 4.01 14.49 3 12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12H19C19 15.86 15.86 19 12 19C8.14 19 5 15.86 5 12C5 8.14 8.14 5 12 5C13.93 5 15.68 5.79 16.95 7.05L14 10H21Z" fill="#374957"/>
            </svg>
            Duration
          </label>
          <div class="date-range-row">
            <div class="date-field">
              <span class="date-prefix">from</span>
              <el-date-picker
                v-model="formData.startDate"
                type="date"
                placeholder="Start date"
                class="date-picker"
                popper-class="medication-date-picker-popper"
                style="width: 255px"
                size="large"
                format="MM/DD/YYYY"
                value-format="YYYY-MM-DD"
              />
            </div>
            <div class="date-field">
              <span class="date-prefix">to</span>
              <el-date-picker
                v-model="formData.endDate"
                type="date"
                placeholder="End date"
                class="date-picker"
                popper-class="medication-date-picker-popper"
                style="width: 270px"
                size="large"
                format="MM/DD/YYYY"
                value-format="YYYY-MM-DD"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <ResponsiveButton
        display_name="Cancel"
        button_type="outlined"
        built_in_theme="error"
        size="medium"
        @click-button="closeDialog"
      />
      <ResponsiveButton
        display_name="Save"
        button_type="filled"
        built_in_theme="dark"
        size="medium"
        :disabled="!isFormValid"
        @click-button="saveMedication"
      />
    </template>
  </Dialog>
</template>

<script>
import { searchDrugsByPrefix, addMedicationRecord, updateMedicationRecord } from '@/api/medicationApi';
import { notify } from '@/utils/notify';

export default {
  name: 'MedicationFormDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    patientInfo: {
      type: Object,
      default: () => ({})
    },
    medication: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      formData: {
        medicationName: '',
        dose: '',
        frequencyNumber: '',
        frequencyPeriod: '',
        startDate: '',
        endDate: ''
      },
      commonMedications: [
        'Aspirin',
        'Lipitor (Atorvastatin)',
        'Metformin',
        'Lisinopril',
        'Amlodipine',
        'Metoprolol',
        'Omeprazole',
        'Simvastatin',
        'Losartan',
        'Gabapentin',
        'Hydrochlorothiazide',
        'Furosemide',
        'Pantoprazole',
        'Prednisone',
        'Ibuprofen',
        'Levothyroxine',
        'Amoxicillin',
        'Azithromycin',
        'Ciprofloxacin',
        'Cephalexin'
      ],
      frequencyPeriodOptions: [
        { label: 'per day', value: 'per day' },
        { label: 'per 12 hours', value: 'per 12 hours' },
        { label: 'per week', value: 'per week' },
        { label: 'per month', value: 'per month' }
      ]
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    isEditMode() {
      return this.medication !== null;
    },
    isFormValid() {
      return this.formData.medicationName;
    }
  },
  watch: {
    medication: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.formData = {
            medicationName: newVal.name || '',
            dose: newVal.dose || '',
            frequencyNumber: newVal.frequencyNumber || '',
            frequencyPeriod: newVal.frequencyPeriod || '',
            startDate: newVal.startDate || '',
            endDate: newVal.endDate || ''
          };
        } else {
          this.resetForm();
        }
      }
    },
    visible(newVal) {
      if (newVal && !this.isEditMode) {
        this.resetForm();
      }
    }
  },
  methods: {
    async queryMedications(queryString, cb) {
      // Only search if we have at least 1 character input
      if (!queryString || queryString.length < 1) {
        cb([]);
        return;
      }

      try {
        const drugNames = await searchDrugsByPrefix(queryString);
        // Use fallback data if API returns empty array (staging/development)
        const results = drugNames.length > 0
          ? drugNames.map(name => ({ value: name }))
          : this.commonMedications
              .filter(med => med.toLowerCase().includes(queryString.toLowerCase()))
              .map(med => ({ value: med }));
        cb(results);
      } catch (error) {
        console.error('Error searching medications:', error);
        // Fallback to common medications on error
        const fallbackResults = this.commonMedications
          .filter(med => med.toLowerCase().includes(queryString.toLowerCase()))
          .map(med => ({ value: med }));
        cb(fallbackResults);
      }
    },
    handleMedicationSelect(item) {
      this.formData.medicationName = item.value;
    },
    handleMedicationClear() {
      this.formData.medicationName = '';
    },
    closeDialog() {
      this.$emit('close');
    },
    async saveMedication() {
      if (!this.isFormValid) return;

      const patientId = this.patientInfo?.patientBasicInfo?.patientId || this.patientInfo?.patientId || this.patientInfo?.id;
      if (!patientId) {
        notify({
          title: 'Error',
          message: 'Patient ID is required',
          type: 'error',
          duration: 3000
        });
        return;
      }

      try {
        if (this.isEditMode && this.medication?.id) {
          // Update existing medication - always send all fields (empty string if cleared)
          const updateData = {
            medication_name: this.formData.medicationName,
            dosage: this.formData.dose || '',
            frequency_value: this.formData.frequencyNumber || '',
            frequency_period: this.formData.frequencyPeriod || '',
            start_date: this.formData.startDate || '',
            end_date: this.formData.endDate || ''
          };

          await updateMedicationRecord(patientId, this.medication.id, updateData);

          // Success notification
          notify({
            title: 'Success',
            message: 'Medication Updated Successfully',
            type: 'success',
            duration: 3000
          });

          this.$emit('save', {
            id: this.medication.id,
            name: this.formData.medicationName,
            dose: this.formData.dose,
            frequencyNumber: this.formData.frequencyNumber,
            frequencyPeriod: this.formData.frequencyPeriod,
            startDate: this.formData.startDate,
            endDate: this.formData.endDate,
            isActive: this.medication.isActive
          });
        } else {
          // Add new medication
          const medicationData = {
            medication_name: this.formData.medicationName,
            dosage: this.formData.dose,
            frequency_value: this.formData.frequencyNumber,
            frequency_period: this.formData.frequencyPeriod,
            start_date: this.formData.startDate,
            end_date: this.formData.endDate
          };

          await addMedicationRecord(patientId, medicationData);

          // Success notification
          notify({
            title: 'Success',
            message: 'Medication Added Successfully',
            type: 'success',
            duration: 3000
          });

          this.$emit('save', {
            name: medicationData.medication_name,
            dose: medicationData.dosage,
            frequencyNumber: medicationData.frequency_value,
            frequencyPeriod: medicationData.frequency_period,
            startDate: medicationData.start_date,
            endDate: medicationData.end_date,
            isActive: true
          });
        }

        this.resetForm();
        this.$emit('close');
      } catch (error) {
        console.error('Failed to save medication:', error);
        notify({
          title: 'Error',
          message: this.isEditMode ? 'Failed to Update Medication' : 'Failed to Save Medication',
          type: 'error',
          duration: 3000
        });
      }
    },
    resetForm() {
      this.formData = {
        medicationName: '',
        dose: '',
        frequencyNumber: '',
        frequencyPeriod: '',
        startDate: '',
        endDate: ''
      };
    }
  }
};
</script>

<style>
/* Medication autocomplete dropdown styling */
.medication-autocomplete-popper .el-autocomplete__popper {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
}

.medication-autocomplete-popper .el-autocomplete-suggestion li {
  font-size: 14px;
  padding: 0 12px;
  line-height: 40px;
}

.medication-autocomplete-popper .el-autocomplete-suggestion li.highlighted {
  background-color: #F5F7FA;
}

.medication-date-picker-popper.el-popper {
  z-index: 3100 !important;
}
</style>

<style scoped>
.medication-form-body {
  padding-top: 24px;
  padding-bottom: 8px;
}

/* 2x3 Grid Layout */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Move frequency selector down to align with input */
.frequency-selector {
  margin-top: 32px;
}

/* Full-width row for Duration */
.form-field-row {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.date-range-row {
  display: flex;
  gap: 16px;
}

.date-field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-prefix {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4A4A4A;
  white-space: nowrap;
}

.field-label {
  overflow: hidden;
  color: var(--color-Grey-Grey-4A, #4A4A4A);
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

/* Form inputs - consistent border #C4C4C4 */
:deep(.el-input .el-input__wrapper) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
}

:deep(.el-input .el-input__wrapper:hover),
:deep(.el-input .el-input__wrapper:focus),
:deep(.el-input .el-input__wrapper.is-focus) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
}

:deep(.el-date-editor .el-input__wrapper) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
}

:deep(.el-date-editor .el-input__wrapper:hover),
:deep(.el-date-editor .el-input__wrapper:focus),
:deep(.el-date-editor .el-input__wrapper.is-focus) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
}

/* Select dropdown borders */
:deep(.new-libary-single-selector) {
  height: 40px;
}

:deep(.new-libary-single-selector .el-select__wrapper) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
  height: 40px !important;
  min-height: 40px !important;
}

:deep(.new-libary-single-selector .el-select__wrapper:hover),
:deep(.new-libary-single-selector .el-select__wrapper:focus),
:deep(.new-libary-single-selector .el-select__wrapper.is-focus) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
  height: 40px !important;
}

/* Placeholder color - gray (when nothing is selected) */
:deep(.new-libary-single-selector .el-select__wrapper.is-transparent) {
  color: #999 !important;
}

/* Selected value text color - black (when value is selected) */
:deep(.new-libary-single-selector .el-select__wrapper:not(.is-transparent)) {
  color: #000 !important;
}

/* Medication name autocomplete input */
:deep(.medication-form-select .el-input__wrapper) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
  height: 40px !important;
}

:deep(.medication-form-select .el-input__wrapper:hover),
:deep(.medication-form-select .el-input__wrapper:focus),
:deep(.medication-form-select .el-input__wrapper.is-focus) {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
}

/* Placeholder styling for autocomplete */
:deep(.medication-form-select .el-input__inner::placeholder) {
  font-size: 14px !important;
  color: #999 !important;
}

/* Search icon styling */
:deep(.medication-form-select .el-input__suffix) {
  display: flex;
  align-items: center;
}

:deep(.medication-form-select .search-icon) {
  margin-right: 8px;
}

/* Selected value text color - black (only when value is selected) */
:deep(.medication-form-select .el-select__wrapper .el-select__selected-item span) {
  color: #000 !important;
}

/* Required text styling */
.field-label {
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-overflow: ellipsis;
}

.required-text {
  color: #D10000;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}
</style>
